import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useUpdateImportMethod from "@babackapp/private-api-client/client/use-update-import-method";
import { ShopImportDto } from "@babackapp/private-api-client/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { PropsWithChildren, useMemo } from "react";
import ImportSettingsForm from "./import-settings-form";
import { ShopImportFormValues, ShopImportFormValuesSchema } from "./import-settings-form-types";
import { z } from "zod";
import { mappingFormSchemaToMappingDto } from "@/pages/(app)/integrations/lib/mapping-form-schema-to-mapping-dto.ts";

type UpdateImportSettingsDialogProps = {
  shopImport: ShopImportDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function mappingDtoToFormSchema(
  mappingDto: ShopImportDto["mapping"],
): z.infer<typeof ShopImportFormValuesSchema>["mapping"] {
  const mapping: z.infer<typeof ShopImportFormValuesSchema>["mapping"] = [];
  if (!mappingDto) return mapping;
  Object.entries(mappingDto).forEach(([mapTo, fieldIndex]) => {
    if (fieldIndex !== null) {
      mapping.push({
        mapTo,
        fieldIndex,
      });
    }
  });
  return mapping;
}

export default function UpdateImportSettingsDialog({
  shopImport,
  children,
  open,
  onOpenChange,
}: PropsWithChildren<UpdateImportSettingsDialogProps>) {
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateImport } = useUpdateImportMethod(shop);

  const onValidate = () => {};

  const onSubmit = ({
    options: { path, settings },
    server: { login, type },
    mapping
  }: ShopImportFormValues) => {
    const next = { ...shopImport };
    next.mapping = mappingFormSchemaToMappingDto(mapping);
    next.login.host = login.host;
    next.login.user = login.user;
    next.login.password = login.password;
    next.login.path = path;
    next.type = type;
    next.settings = settings;

    updateImport({
      importId: shopImport.jobId,
      shopImport: next,
    });
  };

  const initialMappings = useMemo(() => {
    return mappingDtoToFormSchema(shopImport.mapping);
  }, [shopImport.mapping]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[600px]" onPointerDownOutside={(ev) => ev.preventDefault()}>
        <DialogHeader>
          <div className="text-lg leading-6 text-left font-medium text-gray-900">
            Update import settings
          </div>
        </DialogHeader>
        <ImportSettingsForm
          onValidate={onValidate}
          onSubmit={onSubmit}
          defaultValues={{
            server: {
              login: {
                host: shopImport.login?.host,
                user: shopImport.login?.user,
                password: shopImport.login?.password,
                path: shopImport.login?.path,
              },
              type: shopImport.type as "ftp" | "sftp",
            },
            options: {
              path: shopImport.login?.path,
              settings: shopImport.settings,
            },
            mapping: [],
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
